import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  Cloud, ShieldCheck, ArrowLeft, Loader2, RefreshCw, Save,
  CheckCircle2, XCircle, Play, HardDrive, Info,
} from "lucide-react";

const DEST_OPTIONS = [
  { value: "s3",        label: "AWS S3",                hint: "Standard AWS S3 bucket. Leave endpoint URL blank." },
  { value: "s3_compat", label: "S3-Compatible",         hint: "Wasabi / Backblaze B2 / MinIO / DO Spaces. Add endpoint URL." },
  { value: "gcs",       label: "Google Cloud Storage",  hint: "Enable S3 interoperability keys in GCS. Endpoint: https://storage.googleapis.com" },
  { value: "azure_blob",label: "Azure Blob (S3 gateway)", hint: "Point at your Azure S3-compatible gateway endpoint." },
];

export default function DataHomePage() {
  const [loading, setLoading] = useState(true);
  const [cfg, setCfg] = useState(null);          // masked config from server
  const [testing, setTesting] = useState(false);
  const [running, setRunning] = useState(false);
  const [runs, setRuns] = useState([]);
  // Form fields — pre-filled from server for non-secret bits; secrets are re-entered.
  const [enabled, setEnabled] = useState(false);
  const [destinationType, setDestinationType] = useState("s3");
  const [bucket, setBucket] = useState("");
  const [region, setRegion] = useState("eu-west-2");
  const [endpointUrl, setEndpointUrl] = useState("");
  const [pathPrefix, setPathPrefix] = useState("servicesell");
  const [accessKeyId, setAccessKeyId] = useState("");
  const [secretAccessKey, setSecretAccessKey] = useState("");
  const [targetHour, setTargetHour] = useState(2);
  const [notificationEmail, setNotificationEmail] = useState("");
  const [saving, setSaving] = useState(false);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [{ data: c }, { data: r }] = await Promise.all([
        api.get("/data-home/config"),
        api.get("/data-home/runs"),
      ]);
      setCfg(c);
      setRuns(r.runs || []);
      if (c && c.configured) {
        setEnabled(!!c.enabled);
        setDestinationType(c.destination_type || "s3");
        setBucket(c.bucket || "");
        setRegion(c.region || "eu-west-2");
        setEndpointUrl(c.endpoint_url || "");
        setPathPrefix(c.path_prefix || "servicesell");
        setTargetHour(c.target_hour_utc ?? 2);
        setNotificationEmail(c.notification_email || "");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load Data Home settings");
    } finally { setLoading(false); }
  };

  useEffect(() => { loadAll(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        enabled,
        destination_type: destinationType,
        bucket: bucket.trim(),
        region: region.trim(),
        endpoint_url: endpointUrl.trim(),
        path_prefix: pathPrefix.trim() || "servicesell",
        target_hour_utc: Number(targetHour) || 2,
        notification_email: notificationEmail.trim(),
      };
      // Only send secrets if the user has re-entered them (avoid clobber).
      if (accessKeyId.trim()) payload.access_key_id = accessKeyId.trim();
      if (secretAccessKey.trim()) payload.secret_access_key = secretAccessKey.trim();
      // First save must include secrets — reject otherwise.
      if (!cfg?.configured && (!payload.access_key_id || !payload.secret_access_key)) {
        toast.error("Both access key and secret key are required on first save");
        setSaving(false);
        return;
      }
      const { data } = await api.post("/data-home/config", payload);
      setCfg(data);
      setAccessKeyId("");
      setSecretAccessKey("");
      toast.success("Data Home settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const testConn = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/data-home/test");
      if (data.ok) toast.success(data.message || "Connected. Bucket reachable.");
      else toast.error("Connection failed: " + (data.error || "unknown"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test failed");
    } finally { setTesting(false); }
  };

  const runNow = async () => {
    if (!confirm("Run an export now? This will push today's data to your bucket.")) return;
    setRunning(true);
    try {
      const { data } = await api.post("/data-home/run-now");
      if (data.ok) {
        toast.success(`Export complete. ${data.bytes_uploaded?.toLocaleString?.() || 0} bytes uploaded.`);
      } else if (data.skipped) {
        toast.info("Skipped — " + data.skipped);
      } else {
        toast.error("Export failed: " + (data.error || "unknown"));
      }
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Run failed");
    } finally { setRunning(false); }
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="border-b border-white/10 sticky top-0 z-30 backdrop-blur bg-background/85">
        <div className="max-w-5xl mx-auto px-6 h-14 flex items-center justify-between">
          <Link to="/dashboard" className="inline-flex items-center gap-1.5 text-sm text-white/60 hover:text-white" data-testid="data-home-back">
            <ArrowLeft className="w-4 h-4" /> Back to dashboard
          </Link>
          <div className="inline-flex items-center gap-2 text-[10px] font-mono uppercase tracking-wider text-primary border border-primary/40 rounded px-2 py-0.5">
            <Cloud className="w-3.5 h-3.5" /> Data Home
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-10 space-y-8" data-testid="data-home-page">
        {/* Hero */}
        <div>
          <div className="label-overline text-primary">Enterprise · GDPR-ready</div>
          <h1 className="font-display font-black text-4xl tracking-tight mt-1">Your data. Your cloud.</h1>
          <p className="text-muted-foreground mt-3 max-w-2xl">
            Every night we mirror your workshop&apos;s jobs, messages, quotes, photos and audit trail into a bucket
            <strong className="text-foreground"> you own</strong>. If ServiceSell PRO disappeared tomorrow, you&apos;d still have every quote and photo
            in your own cloud. Point your BI tools straight at the bucket — no API tickets, no waiting.
          </p>
        </div>

        {/* Status card */}
        <div className="card-default p-6 flex items-start gap-5" data-testid="data-home-status">
          <div className={`w-14 h-14 rounded-full grid place-items-center shrink-0 ${cfg?.enabled ? "bg-success/15" : "bg-white/[0.04]"}`}>
            {cfg?.enabled ? <ShieldCheck className="w-8 h-8 text-success" /> : <HardDrive className="w-8 h-8 text-white/40" />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-lg">
              {loading ? "Loading…" : cfg?.enabled ? "Nightly export is active" : cfg?.configured ? "Configured but paused" : "Not yet configured"}
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              {cfg?.enabled
                ? <>Fires at <span className="font-mono-plex">{String(cfg.target_hour_utc || 2).padStart(2, "0")}:00 UTC</span> every day into <span className="font-mono-plex">{cfg.bucket}</span>. Latest run below.</>
                : cfg?.configured
                  ? "Settings saved. Toggle 'Enable nightly export' below to start pushing data."
                  : "Add your cloud bucket + credentials below. First save must include both access key and secret."}
            </p>
          </div>
          <div className="flex gap-2 shrink-0">
            <button onClick={testConn} disabled={testing || !cfg?.configured} className="btn-secondary text-sm" data-testid="data-home-test-btn">
              {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />} Test connection
            </button>
            <button onClick={runNow} disabled={running || !cfg?.enabled} className="btn-primary text-sm" data-testid="data-home-run-now-btn">
              {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />} Run now
            </button>
          </div>
        </div>

        {/* Config form */}
        <div className="card-default p-6 space-y-5" data-testid="data-home-config">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-semibold text-lg">Cloud destination</div>
              <div className="text-xs text-muted-foreground mt-0.5">All credentials are encrypted at rest. Secrets are never returned in API responses.</div>
            </div>
            <label className="inline-flex items-center gap-2 cursor-pointer" data-testid="data-home-enable-toggle">
              <input type="checkbox" className="peer sr-only" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
              <span className="relative w-10 h-6 bg-white/10 rounded-full transition peer-checked:bg-primary">
                <span className="absolute left-0.5 top-0.5 w-5 h-5 rounded-full bg-white transition peer-checked:translate-x-4" />
              </span>
              <span className="text-sm font-medium">Enable nightly export</span>
            </label>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Destination type" hint={DEST_OPTIONS.find(o => o.value === destinationType)?.hint}>
              <select className="input" value={destinationType} onChange={(e) => setDestinationType(e.target.value)} data-testid="data-home-destination-select">
                {DEST_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </Field>
            <Field label="Bucket name" hint="Must exist in your cloud account.">
              <input className="input font-mono-plex" placeholder="rrg-servicesell-datalake" value={bucket} onChange={(e) => setBucket(e.target.value)} data-testid="data-home-bucket-input" />
            </Field>
            <Field label="Region" hint="AWS region code — eu-west-2 is London.">
              <input className="input font-mono-plex" value={region} onChange={(e) => setRegion(e.target.value)} data-testid="data-home-region-input" />
            </Field>
            <Field label="Endpoint URL (S3-compatible only)" hint="Leave blank for AWS S3.">
              <input className="input font-mono-plex" placeholder="https://s3.wasabisys.com" value={endpointUrl} onChange={(e) => setEndpointUrl(e.target.value)} data-testid="data-home-endpoint-input" />
            </Field>
            <Field label="Path prefix" hint="Objects land under this prefix.">
              <input className="input font-mono-plex" value={pathPrefix} onChange={(e) => setPathPrefix(e.target.value)} data-testid="data-home-prefix-input" />
            </Field>
            <Field label="Target hour (UTC)" hint="0–23. Default 02:00 UTC = 02:00 GMT / 03:00 BST.">
              <input className="input font-mono-plex" type="number" min="0" max="23" value={targetHour} onChange={(e) => setTargetHour(e.target.value)} data-testid="data-home-target-hour" />
            </Field>
            <Field label="Access Key ID" hint={cfg?.configured ? `Currently: ${cfg.access_key_id_masked}. Leave blank to keep unchanged.` : "Required on first save."}>
              <input className="input font-mono-plex" placeholder={cfg?.configured ? "•••• (unchanged)" : "AKIA…"} value={accessKeyId} onChange={(e) => setAccessKeyId(e.target.value)} data-testid="data-home-access-key-input" />
            </Field>
            <Field label="Secret Access Key" hint={cfg?.secret_present ? "Stored. Leave blank to keep unchanged." : "Required on first save."}>
              <input className="input font-mono-plex" type="password" placeholder={cfg?.secret_present ? "•••• (unchanged)" : "wJal…"} value={secretAccessKey} onChange={(e) => setSecretAccessKey(e.target.value)} data-testid="data-home-secret-key-input" />
            </Field>
            <Field label="Failure notification email" hint="If a nightly export fails we'll email this address." className="md:col-span-2">
              <input className="input" type="email" placeholder="data-team@rrg-group.com" value={notificationEmail} onChange={(e) => setNotificationEmail(e.target.value)} data-testid="data-home-notify-input" />
            </Field>
          </div>

          <div className="pt-2 border-t border-white/10 flex items-center justify-between">
            <div className="text-xs text-muted-foreground flex items-center gap-1.5">
              <Info className="w-3.5 h-3.5" />
              Server-side encryption is applied to every uploaded object. Data in transit is TLS-only.
            </div>
            <button onClick={save} disabled={saving} className="btn-primary" data-testid="data-home-save-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Save settings
            </button>
          </div>
        </div>

        {/* Recent runs */}
        <div className="card-default p-6" data-testid="data-home-runs">
          <div className="flex items-center justify-between mb-3">
            <div className="font-semibold text-lg">Recent exports</div>
            <button onClick={loadAll} className="btn-ghost text-xs" data-testid="data-home-refresh-runs">
              <RefreshCw className="w-3.5 h-3.5" /> Refresh
            </button>
          </div>
          {runs.length === 0 ? (
            <div className="text-sm text-muted-foreground italic">
              No exports yet. Enable nightly export and either wait for {String(targetHour).padStart(2, "0")}:00 UTC, or hit &ldquo;Run now&rdquo; above.
            </div>
          ) : (
            <div className="text-xs font-mono-plex space-y-1.5">
              <div className="grid grid-cols-12 gap-2 text-white/40 uppercase tracking-wider text-[10px] pb-1 border-b border-white/10">
                <div className="col-span-3">Timestamp</div>
                <div className="col-span-1">OK</div>
                <div className="col-span-2">Bytes</div>
                <div className="col-span-6">Bucket path</div>
              </div>
              {runs.map((r) => (
                <div key={r.id} className="grid grid-cols-12 gap-2 items-center py-1 border-b border-white/[0.04]">
                  <div className="col-span-3 text-white/75">{new Date(r.run_at).toLocaleString("en-GB")}</div>
                  <div className="col-span-1">{r.ok ? <CheckCircle2 className="w-4 h-4 text-success" /> : <XCircle className="w-4 h-4 text-danger" />}</div>
                  <div className="col-span-2">{(r.bytes_uploaded || 0).toLocaleString()}</div>
                  <div className="col-span-6 text-white/60 truncate">{r.prefix}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

function Field({ label, hint, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <div className="text-[11px] font-bold uppercase tracking-wider text-white/50 mb-1">{label}</div>
      {children}
      {hint && <div className="text-[11px] text-white/40 mt-1 leading-relaxed">{hint}</div>}
    </label>
  );
}
